import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import { exportToCsv, type ColumnConfig } from "shared/common/utils/export";
import { findOrThrow, loadErrorMessage } from "shared/loadOrError";
import { isSessionEnded } from "stores/sessionGuard";

/**
 * View store for the Clients → Client Risk view.
 *
 * Rule: route ViewStores are plain classes with `makeAutoObservable` and do NOT
 * extend ViewStore. The read-only `client-blockers` service is now a registered
 * `@intrasync/stores` ServiceStore (see serviceStores/clients.stores.ts); per
 * the MobX rule the blocker rows live in that ServiceStore's entity cache, not
 * in this ViewStore — `rows` is a computed view over it.
 *
 * The API service registered at `client-blockers` is a find-only custom service.
 * Each row groups one client's open implementation blockers (dead/not-running
 * instances, missed milestones, overdue tasks, stalled onboarding), pre-sorted
 * worst-first by the API and uniquely keyed by `clientId`. The "Client Risk"
 * surface ranks clients by their worst blocker so the team can triage the
 * accounts most in danger at a glance.
 */

/** Blocker severity tiers, worst-first. */
export type BlockerSeverity = "high" | "medium" | "low";

export const BLOCKER_SEVERITIES: BlockerSeverity[] = ["high", "medium", "low"];

/** UI metadata per severity: label + MUI chip color. */
export const BLOCKER_SEVERITY_META: Record<
  BlockerSeverity,
  { label: string; color: "error" | "warning" | "info" }
> = {
  high: { label: "High", color: "error" },
  medium: { label: "Medium", color: "warning" },
  low: { label: "Low", color: "info" },
};

export const blockerSeverityLabel = (severity?: string | null): string =>
  (severity && BLOCKER_SEVERITY_META[severity as BlockerSeverity]?.label) ||
  severity ||
  "";

export const blockerSeverityColor = (
  severity?: string | null
): "error" | "warning" | "info" | "default" =>
  (severity && BLOCKER_SEVERITY_META[severity as BlockerSeverity]?.color) ||
  "default";

/** The blocker types the API can attach to a client. */
export type BlockerType =
  | "dead-instance"
  | "not-running-instance"
  | "onboarding-stalled";

/** "all" plus each severity — the header's top-severity filter options. */
export type BlockerSeverityFilter = "all" | BlockerSeverity;

/** One blocker the API attaches to a client (pre-sorted worst-first). */
export interface ClientBlocker {
  type: BlockerType | string;
  severity: BlockerSeverity | string;
  count: number;
  label: string;
}

/**
 * One client's blocker summary as the API serialises it. `blockers` is
 * pre-sorted worst-first; `topSeverity` mirrors `blockers[0].severity` and is
 * null when the client has no open blockers.
 */
export interface ClientBlockers {
  clientId: string;
  clientName: string;
  blockers: ClientBlocker[];
  blockerCount: number;
  topSeverity: BlockerSeverity | null;
}

/** Rank weight for each severity (higher == worse) used to sort rows. */
const SEVERITY_RANK: Record<BlockerSeverity, number> = {
  high: 3,
  medium: 2,
  low: 1,
};

const severityRank = (severity?: BlockerSeverity | null): number =>
  severity ? SEVERITY_RANK[severity] : 0;

/** The `{ total, data }` envelope `client-blockers` find returns. */
interface BlockersFindResult {
  total?: number;
  data?: ClientBlockers[];
}

// ---------------------------------------------------------------------------
// CSV export of the risk table
// ---------------------------------------------------------------------------

/** One row of the Client Risk CSV. */
export interface ClientRiskExportRow extends Record<string, unknown> {
  clientName: string;
  clientId: string;
  topSeverity: string;
  blockerCount: number;
  blockers: string;
}

/**
 * CSV shape of the Client Risk table. No `format` on the count column — the
 * toolkit's number formatter emits a locale string a spreadsheet re-imports as
 * text, and these are small integers anyway.
 */
export const CLIENT_RISK_EXPORT_COLUMNS: ColumnConfig[] = [
  { key: "clientName", label: "Client" },
  { key: "clientId", label: "Client Id" },
  { key: "topSeverity", label: "Top Severity" },
  { key: "blockerCount", label: "Blockers" },
  { key: "blockers", label: "Blocker Detail" },
];

/**
 * Map ranked risk rows to export records. PURE — no store, no download — so the
 * shaping is unit-testable on its own.
 *
 * The Detail column flattens the chip row the table renders, keeping the API's
 * worst-first order: `severity: label (count)`, joined with "; ". A client with
 * no open blockers exports an empty severity rather than the "None" chip label,
 * so a spreadsheet filter on severity is not polluted with a UI word.
 */
export function buildClientRiskExportRows(
  rows: ClientBlockers[]
): ClientRiskExportRow[] {
  return rows.map((row) => ({
    clientName: row.clientName ?? "",
    clientId: row.clientId ?? "",
    topSeverity: row.topSeverity ?? "",
    blockerCount: row.blockerCount ?? 0,
    blockers: (row.blockers ?? [])
      .map((b) => `${b.severity}: ${b.label} (${b.count})`)
      .join("; "),
  }));
}

class ClientRiskStore {
  rootStore: RootStore;

  loading = false;
  error: string | null = null;

  /**
   * `total` as the last find reported it, or null when no load has succeeded.
   * The service currently returns every client in one response (total ===
   * data.length), so this normally matches {@link rows}; it is read anyway so
   * that the day the API starts capping rows, the table says so instead of
   * quietly shrinking every summary card. See {@link truncationNotice}.
   */
  serverTotal: number | null = null;

  // Header filters
  searchQuery = "";
  severityFilter: BlockerSeverityFilter = "all";

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  /** The registered `client-blockers` ServiceStore (clients.stores.ts). */
  private get blockersService(): ServiceStoreType | null {
    return (this.rootStore.clientBlockersStore as ServiceStoreType | null) ?? null;
  }

  /**
   * Blocker rows. Per the MobX rule, entities live in the ServiceStore cache
   * (populated by `find`), not in this ViewStore — this is a computed view.
   * Each row is keyed by `clientId` (the service has no `id`).
   *
   * EMPTY while `error` is set: the entity cache may still hold a previous
   * load, and rows (and every card derived from them) under a "failed to
   * load" message would present stale risk as current.
   */
  get rows(): ClientBlockers[] {
    if (this.error) return [];
    return (this.blockersService?.entities ?? []) as unknown as ClientBlockers[];
  }

  /** True when the header's search or severity filter narrows the table. */
  get hasActiveFilters(): boolean {
    return this.searchQuery.trim() !== "" || this.severityFilter !== "all";
  }

  // ---- Derived data -------------------------------------------------------

  /**
   * Rows ranked by risk: worst top-severity first, then by blocker count, then
   * by name for a stable tie-break. The search + severity filters are applied
   * first.
   */
  get rankedRows(): ClientBlockers[] {
    let result = this.rows;

    if (this.severityFilter !== "all") {
      result = result.filter((r) => r.topSeverity === this.severityFilter);
    }

    if (this.searchQuery.trim()) {
      const q = this.searchQuery.toLowerCase().trim();
      result = result.filter(
        (r) =>
          r.clientName?.toLowerCase().includes(q) ||
          r.clientId?.toLowerCase().includes(q) ||
          (r.blockers || []).some(
            (b) =>
              b.label?.toLowerCase().includes(q) ||
              b.type?.toLowerCase().includes(q)
          )
      );
    }

    // Copy before sort so MobX's observable array order is not mutated.
    return [...result].sort((a, b) => {
      const sev = severityRank(b.topSeverity) - severityRank(a.topSeverity);
      if (sev !== 0) return sev;
      const count = (b.blockerCount || 0) - (a.blockerCount || 0);
      if (count !== 0) return count;
      return (a.clientName || "").localeCompare(b.clientName || "");
    });
  }

  get totalCount(): number {
    return this.rows.length;
  }

  /** Clients carrying at least one high-severity blocker. */
  get atRiskCount(): number {
    return this.rows.filter((r) =>
      (r.blockers || []).some((b) => b.severity === "high")
    ).length;
  }

  /** Total open blockers across every client. */
  get totalBlockers(): number {
    return this.rows.reduce((sum, r) => sum + (r.blockerCount || 0), 0);
  }

  get highSeverityCount(): number {
    return this.rows.filter((r) => r.topSeverity === "high").length;
  }

  get mediumSeverityCount(): number {
    return this.rows.filter((r) => r.topSeverity === "medium").length;
  }

  get lowSeverityCount(): number {
    return this.rows.filter((r) => r.topSeverity === "low").length;
  }

  /**
   * Caption for the case where the server returned fewer rows than it counted.
   *
   * Every summary card on this page is derived from the LOADED rows, so if the
   * response were ever capped, "Clients", "At Risk", "Total Blockers" and the
   * severity counts would all silently understate the real picture. `total` is
   * the server's own count, so the two can be compared and the difference said
   * out loud — the same shape as the Data Integrity view's caption.
   */
  get truncationNotice(): string | null {
    const total = this.serverTotal;
    const loaded = this.rows.length;
    if (total === null || total <= loaded) return null;
    return `Showing ${loaded} of ${total} clients — the counts above cover the ${loaded} loaded rows only.`;
  }

  // ---- CSV export ---------------------------------------------------------

  /**
   * What "Export CSV" writes: the rows currently ON SCREEN, in screen order —
   * the severity filter and the search applied, ranked worst-first exactly as
   * the table ranks them.
   */
  get exportRows(): ClientRiskExportRow[] {
    return buildClientRiskExportRows(this.rankedRows);
  }

  get canExport(): boolean {
    return this.exportRows.length > 0;
  }

  /**
   * Download the filtered risk table as CSV. Returns false instead of throwing
   * when there is nothing to export (the toolkit throws on an empty set) so the
   * header can stay a plain onClick.
   */
  exportCsv(): boolean {
    const rows = this.exportRows;
    if (rows.length === 0) return false;
    try {
      exportToCsv(rows, CLIENT_RISK_EXPORT_COLUMNS, {
        fileName: `client-risk-${new Date().toISOString().slice(0, 10)}`,
      });
      return true;
    } catch (err) {
      console.error("client risk: CSV export failed", err);
      return false;
    }
  }

  // ---- Filter setters -----------------------------------------------------

  setSearchQuery(q: string) {
    this.searchQuery = q;
  }

  setSeverityFilter(filter: BlockerSeverityFilter) {
    this.severityFilter = filter;
  }

  // ---- Loading ------------------------------------------------------------

  async load() {
    this.loading = true;
    this.error = null;
    try {
      // NO QUERY PARAMS. `clientBlockersQuerySchema` is additionalProperties:
      // false with ONLY an optional clientId, so a `$limit` here does not cap
      // anything — it is REJECTED (400 "must NOT have additional properties")
      // and the whole page loads empty. Same contract as client-summary /
      // client-health (see ClientsStore.loadRollups). The service returns one
      // row per non-deleted client plus its own `total`, which is read back so
      // a future cap could not go unnoticed.
      // ServiceStore.find populates its entity cache, which `rows` reads.
      // `findOrThrow`: the ServiceStore swallows a failed find (resolves null,
      // records errorOnFind), which used to render "No client risk data
      // available" over tiles of 0 while client-blockers was down.
      const result = await findOrThrow<BlockersFindResult | null>(
        this.blockersService,
        { query: {} },
      );
      runInAction(() => {
        this.serverTotal =
          typeof result?.total === "number" ? result.total : null;
      });
    } catch (err: unknown) {
      // Signed out under this call: what the reset left is the next person's.
      if (isSessionEnded(err)) return;
      runInAction(() => {
        this.error = loadErrorMessage(err, "Failed to load client risk");
        this.serverTotal = null;
      });

      console.error("Failed to load client blockers:", err);
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  /** Alias used by header refresh / post-write callbacks. */
  async reload() {
    await this.load();
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.loading = false;
    this.error = null;
    this.serverTotal = null;
    this.searchQuery = "";
    this.severityFilter = "all";
  }
}

export default ClientRiskStore;
